import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "text-xl leading-8 block font-normal text-gray-500 mt-[1.3333333em]" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("figure", { class: "mt-6" }, [
  /*#__PURE__*/_createElementVNode("img", {
    class: "w-full rounded-lg",
    src: "/img/learning/dns-propagation-does-not-exist/dns-propagation-does-not-exist.png",
    alt: "DNS propagation does not exist",
    title: "DNS propagation does not exist",
    width: "1200",
    height: "600"
  }),
  /*#__PURE__*/_createElementVNode("figcaption", null, "DNS resolution depends on time, not geography.")
], -1)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("h2", null, [
  /*#__PURE__*/_createTextVNode("So how "),
  /*#__PURE__*/_createElementVNode("i", null, "does"),
  /*#__PURE__*/_createTextVNode(" it work?")
], -1)
const _hoisted_4 = /*#__PURE__*/_createElementVNode("p", null, "When you request DNS records from the server that is authoritative for that domain (i.e. who \"owns\" it), it will accompany those records with a TTL (time to live). This indicates how long you can keep using those values before you should request a fresh copy. The records are stored in a cache on your device. Eliminating the need to query the DNS server every time speeds things up and reduces the load on DNS servers.", -1)
const _hoisted_5 = /*#__PURE__*/_createElementVNode("p", null, "Many internet service providers provide DNS caching servers to their subscribers. There are also some public DNS servers, offered by Google, Cloudflare and others. Those are all called recursive DNS servers. They can provide you with an answer by querying the authoritative DNS server. These recursive servers use the TTL to cache records on their side. So there are multiple layers of caching: in these servers and on your device.", -1)
const _hoisted_6 = /*#__PURE__*/_createElementVNode("p", null, "It's of course possible that the records have been changed, and that you get an old version of the records that was still in the cache. These records are said to be stale. So when this happens, it's not caused by records that didn't propagate yet, but by a stale cache.", -1)
const _hoisted_7 = /*#__PURE__*/_createElementVNode("p", null, "Staleness depends on incidental timing of previous requests, and they are not correlated with the geolocation of a DNS server or its place in some physical or logical network.", -1)
const _hoisted_8 = /*#__PURE__*/_createElementVNode("p", null, "Another way to look at it is that records are not pushed (propagated), but pulled (queried and cached).", -1)
const _hoisted_9 = /*#__PURE__*/_createElementVNode("h2", null, "A suggested change in terminology", -1)
const _hoisted_10 = /*#__PURE__*/_createElementVNode("p", null, "Okay, so \"DNS propagation\" may not be the best choice of words. But why does that matter?", -1)
const _hoisted_11 = /*#__PURE__*/_createElementVNode("p", null, "Well, words trigger associations, and they shape the way we think about something. So the terminology we use is important from a pedagogical perspective. When something is said to propagate, someone who hasn't learned how DNS works will assume that it somehow spreads through a network. And everyone who does know how it works had to have learned it at some point. It will be easier to learn if we use words that trigger the right associations.", -1)
const _hoisted_12 = /*#__PURE__*/_createElementVNode("p", null, "So let's eliminate this fallacy, and call it cache expiration instead of propagation.", -1)

import BlogPost from '@/components/layout/BlogPost.vue';

import { ruurtjan } from '@/common/authors';
import { BlogMeta } from '@/common/blogMeta';
import { learningBreadcrumb } from '@/model/Breadcrumb';

export const meta: BlogMeta = {
  tag: 'DNS',
  url: '/learning/dns-propagation-does-not-exist/',
  breadcrumbs: [learningBreadcrumb],
  title: 'DNS propagation does not exist',
  description: 'A widespread fallacy among IT professionals is that DNS propagates through some network. But DNS propagation does not exist.',
  overviewDescription: 'A widespread fallacy among IT professionals is that DNS propagates through some network. But DNS propagation does not exist.',
  overviewImage: 'img/learning/dns-propagation-does-not-exist/dns-propagation-does-not-exist-small.png',
  overviewImageAlt: 'DNS propagation does not exist',
  author: ruurtjan,
  img1024x512: 'https://www.nslookup.io/img/learning/dns-propagation-does-not-exist/dns-propagation-does-not-exist-social.png',
  img1200x630: 'https://www.nslookup.io/img/learning/dns-propagation-does-not-exist/dns-propagation-does-not-exist-social.png',
  written: new Date('2021-04-07T22:33:35.579Z'),
  updated: new Date('2023-04-18T07:40:44.129Z'),
};

const __default__ = {
  meta,
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'DnsPropagationDoesNotExist',
  setup(__props) {


return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createBlock(BlogPost, {
    title: meta.title,
    description: meta.description,
    url: `https://www.nslookup.io${meta.url}`,
    breadcrumbs: meta.breadcrumbs,
    img1024x512: meta.img1024x512,
    img1200x630: meta.img1200x630,
    written: meta.written,
    updated: meta.updated,
    author: meta.author,
    editor: meta.editor
  }, {
    default: _withCtx(() => [
      _createElementVNode("strong", _hoisted_1, [
        _createTextVNode("A widespread fallacy among IT professionals is that DNS propagates through some network. So widespread in fact, that there are a couple of "),
        _createVNode(_component_router_link, { to: "/dns-checker/" }, {
          default: _withCtx(() => [
            _createTextVNode("DNS checkers")
          ]),
          _: 1
        }),
        _createTextVNode(" dedicated to visualizing the geographic propagation of DNS records. But DNS propagation does not exist.")
      ]),
      _hoisted_2,
      _hoisted_3,
      _hoisted_4,
      _hoisted_5,
      _hoisted_6,
      _hoisted_7,
      _hoisted_8,
      _hoisted_9,
      _hoisted_10,
      _hoisted_11,
      _hoisted_12
    ]),
    _: 1
  }, 8, ["title", "description", "url", "breadcrumbs", "img1024x512", "img1200x630", "written", "updated", "author", "editor"]))
}
}

})